"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { animate, motion, useInView, useReducedMotion } from "motion/react";

/*
 * Motion for the recommendation page, in the language PhotoPenFrame already
 * set: things are drawn once, as if by a pen, when they first come into view.
 * Every effect plays a single time and is skipped under reduced motion, where
 * the final state renders straight away.
 */

const EASE = [0.45, 0, 0.2, 1] as const;

/** Saffron marker swept under a phrase, left to right. Drawn as an animated
 *  background so it follows the text across line breaks. */
export function MarkerHighlight({ children, delay = 0.3 }: { children: ReactNode; delay?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const still = useReducedMotion() ?? false;
  return (
    <motion.span
      ref={ref}
      className="font-medium text-[#264653] [box-decoration-break:clone] [-webkit-box-decoration-break:clone]"
      style={{
        backgroundImage: "linear-gradient(rgba(233,196,106,0.7), rgba(233,196,106,0.7))",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "0 88%",
      }}
      initial={still ? false : { backgroundSize: "0% 0.42em" }}
      animate={inView || still ? { backgroundSize: "100% 0.42em" } : undefined}
      transition={{ duration: still ? 0 : 0.8, delay: still ? 0 : delay, ease: EASE }}
    >
      {children}
    </motion.span>
  );
}

/** A number that counts up the first time it is seen. */
export function CountUp({ value, suffix = "", format = (n: number) => n.toLocaleString("ro-RO") }: {
  value: number;
  suffix?: string;
  format?: (n: number) => string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const still = useReducedMotion() ?? false;
  const [shown, setShown] = useState(still ? value : 0);

  useEffect(() => {
    if (still) {
      setShown(value);
      return;
    }
    if (!inView) return;
    const controls = animate(0, value, {
      duration: value > 100 ? 1.2 : 0.9,
      ease: EASE,
      onUpdate: (v) => setShown(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, still, value]);

  return (
    <span ref={ref} className="tabular-nums">
      {format(shown)}
      {suffix}
    </span>
  );
}

/** One soft ring that expands and fades around its child, once. */
export function PulseOnce({ children, delay = 1.4 }: { children: ReactNode; delay?: number }) {
  const still = useReducedMotion() ?? false;
  return (
    <span className="relative inline-flex">
      {!still && (
        <motion.span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-[#2C969B]"
          initial={{ opacity: 0.6, scale: 1 }}
          animate={{ opacity: 0, scale: 1.25 }}
          transition={{ duration: 1.1, delay, ease: "easeOut" }}
        />
      )}
      {children}
    </span>
  );
}

/** A check mark drawn with a pen stroke, staggered by index. */
export function DrawnCheck({ index = 0 }: { index?: number }) {
  const ref = useRef<SVGSVGElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.8 });
  const still = useReducedMotion() ?? false;
  return (
    <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-[#2C969B] text-white">
      <svg ref={ref} viewBox="0 0 12 12" className="size-3" aria-hidden>
        <motion.path
          d="M2.5 6.4 L5 8.7 L9.6 3.4"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.9}
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={still ? false : { pathLength: 0 }}
          animate={inView || still ? { pathLength: 1 } : undefined}
          transition={{ duration: still ? 0 : 0.45, delay: still ? 0 : 0.25 + index * 0.18, ease: EASE }}
        />
      </svg>
    </span>
  );
}

/** Slides a block up a little as it enters, staggered by index. `as="li"`
 *  keeps lists valid. */
export function Rise({
  children,
  index = 0,
  className = "",
  as = "div",
}: {
  children: ReactNode;
  index?: number;
  className?: string;
  as?: "div" | "li";
}) {
  const still = useReducedMotion() ?? false;
  const Tag = as === "li" ? motion.li : motion.div;
  return (
    <Tag
      className={className}
      initial={still ? false : { opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.5, delay: index * 0.12, ease: EASE }}
    >
      {children}
    </Tag>
  );
}

/** The training timeline: the rule draws down, then each entry arrives. */
export function TimelineRule() {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  const still = useReducedMotion() ?? false;
  return (
    <motion.span
      ref={ref}
      aria-hidden
      className="absolute bottom-0 left-0 top-0 w-px origin-top bg-[#2C969B]/35"
      initial={still ? false : { scaleY: 0 }}
      animate={inView || still ? { scaleY: 1 } : undefined}
      transition={{ duration: still ? 0 : 1.2, ease: EASE }}
    />
  );
}

/** A loose pen loop drawn around a figure, like the loops around the faces in
 *  the home hero. Sits absolutely over a `relative inline-block` parent. */
export function PenCircle({ delay = 1.1 }: { delay?: number }) {
  const ref = useRef<SVGSVGElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const still = useReducedMotion() ?? false;
  return (
    <svg
      ref={ref}
      aria-hidden
      viewBox="0 0 200 90"
      preserveAspectRatio="none"
      className="pointer-events-none absolute -inset-x-[12%] -inset-y-[26%] h-[152%] w-[124%] overflow-visible"
    >
      <motion.path
        d="M118 10 C 60 2, 12 18, 10 46 C 8 74, 64 86, 112 82 C 162 78, 194 62, 190 38 C 186 14, 140 4, 92 9"
        fill="none"
        stroke="#DC7050"
        strokeWidth={2.4}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
        initial={still ? false : { pathLength: 0, opacity: 0 }}
        animate={inView || still ? { pathLength: 1, opacity: 1 } : undefined}
        transition={{
          pathLength: { duration: still ? 0 : 0.9, delay: still ? 0 : delay, ease: EASE },
          opacity: { duration: 0.01, delay: still ? 0 : delay },
        }}
      />
    </svg>
  );
}
